<template>
    <div class="team-compliance-dashboard">
        <!-- Dashboard Header -->
        <div class="dashboard-header">
            <div class="header-title">
                <h2>Team Nutrition Compliance</h2>
                <span class="date-range">{{ formatDateRange }}</span>
            </div>

            <div class="header-controls">
                <select v-model="selectedTeam" class="team-select" @change="loadData">
                    <option value="">All Teams</option>
                    <option v-for="team in teams" :key="team.id" :value="team.id">
                        {{ team.name }}
                    </option>
                </select>

                <div class="date-selector">
                    <button class="date-btn" @click="changeWeek(-1)">←</button>
                    <span class="current-week">Week of {{ formatDate(weekStart) }}</span>
                    <button class="date-btn" @click="changeWeek(1)" :disabled="isCurrentWeek">→</button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <span>Loading compliance data...</span>
        </div>

        <template v-else>
            <!-- Summary Cards -->
            <div class="summary-section">
                <div class="summary-card overall">
                    <div class="card-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                    </div>
                    <div class="card-content">
                        <span class="card-value">{{ overallCompliance }}%</span>
                        <span class="card-label">Overall Compliance</span>
                    </div>
                    <div class="card-trend" :class="complianceTrend">
                        {{ complianceTrendIcon }} {{ complianceTrendValue }}%
                    </div>
                </div>

                <div class="summary-card logging">
                    <div class="card-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                        </svg>
                    </div>
                    <div class="card-content">
                        <span class="card-value">{{ loggingRate }}%</span>
                        <span class="card-label">Logging Rate</span>
                    </div>
                    <div class="card-detail">{{ loggedToday }}/{{ totalPlayers }} today</div>
                </div>

                <div class="summary-card hydration">
                    <div class="card-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2">
                            <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
                        </svg>
                    </div>
                    <div class="card-content">
                        <span class="card-value">{{ avgHydration }}%</span>
                        <span class="card-label">Avg Hydration</span>
                    </div>
                    <div class="card-detail">Target: 2-3L daily</div>
                </div>

                <div class="summary-card macros">
                    <div class="card-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2">
                            <path d="M18 20V10"></path>
                            <path d="M12 20V4"></path>
                            <path d="M6 20v-6"></path>
                        </svg>
                    </div>
                    <div class="card-content">
                        <span class="card-value">{{ macroCompliance }}%</span>
                        <span class="card-label">Macro Targets Met</span>
                    </div>
                </div>
            </div>

            <!-- Weekly Compliance Chart -->
            <div class="chart-section">
                <div class="section-header">
                    <h3>Weekly Compliance Trends</h3>
                </div>

                <div class="compliance-chart">
                    <div class="chart-container">
                        <div v-for="day in weeklyData" :key="day.date" class="chart-day"
                            :class="{ today: day.isToday, future: day.isFuture }">
                            <div class="bar-container">
                                <div class="bar-stack">
                                    <div class="bar logging" :style="{ height: day.loggingRate + '%' }"
                                        :title="`Logging: ${day.loggingRate}%`"></div>
                                </div>
                                <div class="bar-value">{{ day.compliance }}%</div>
                            </div>
                            <div class="day-label">{{ day.dayName }}</div>
                            <div class="day-date">{{ day.dateShort }}</div>
                        </div>
                    </div>

                    <div class="chart-legend">
                        <span class="legend-item">
                            <span class="legend-color logging"></span>
                            Logging Rate
                        </span>
                    </div>
                </div>
            </div>

            <!-- Player Compliance Table -->
            <div class="players-section">
                <div class="section-header">
                    <h3>Player Breakdown</h3>
                    <div class="section-controls">
                        <select v-model="sortBy" class="sort-select">
                            <option value="compliance">Sort by Compliance</option>
                            <option value="name">Sort by Name</option>
                            <option value="streak">Sort by Streak</option>
                            <option value="hydration">Sort by Hydration</option>
                        </select>
                        <button class="filter-btn" :class="{ active: showOnlyNeedsAttention }"
                            @click="showOnlyNeedsAttention = !showOnlyNeedsAttention">
                            ⚠️ Needs Attention
                        </button>
                    </div>
                </div>

                <div class="player-grid">
                    <div v-for="player in filteredPlayers" :key="player.id" class="player-card"
                        :class="getPlayerStatusClass(player)" @click="selectPlayer(player)">
                        <div class="player-avatar">
                            <img v-if="player.image" :src="player.image" :alt="player.name" class="avatar-img" />
                            <div v-else class="avatar-placeholder">
                                {{ getInitials(player.name) }}
                            </div>
                            <div class="status-indicator" :class="getPlayerStatusClass(player)"></div>
                        </div>

                        <div class="player-info">
                            <span class="player-name">{{ player.name }}</span>
                            <span class="player-position">{{ player.position }}</span>
                        </div>

                        <div class="player-stats">
                            <div class="stat-row">
                                <span class="stat-label">Compliance</span>
                                <div class="compliance-bar">
                                    <div class="bar-fill" :style="{ width: player.compliance + '%' }"
                                        :class="getComplianceClass(player.compliance)"></div>
                                </div>
                                <span class="stat-value">{{ player.compliance }}%</span>
                            </div>

                            <div class="stat-row">
                                <span class="stat-label">Streak</span>
                                <span class="stat-value streak">
                                    {{ player.streak }} {{ player.streak === 1 ? 'day' : 'days' }}
                                    <span class="streak-icon">🔥</span>
                                </span>
                            </div>

                            <div class="stat-row">
                                <span class="stat-label">Hydration</span>
                                <span class="stat-value" :class="getHydrationClass(player.hydration)">
                                    {{ player.hydration }}%
                                </span>
                            </div>
                        </div>

                        <div class="player-actions">
                            <button class="action-btn message" @click.stop="messagePlayer(player)"
                                title="Send reminder">
                                💬
                            </button>
                            <button class="action-btn view" @click.stop="viewPlayerDetail(player)" title="View details">
                                →
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="filteredPlayers.length === 0" class="no-players">
                    <p>No players match the current filters.</p>
                </div>
            </div>

            <!-- Alerts & Notifications -->
            <div class="alerts-section" v-if="alerts.length > 0">
                <div class="section-header">
                    <h3>⚠️ Alerts</h3>
                    <button class="clear-all-btn" @click="clearAlerts">Clear All</button>
                </div>

                <div class="alerts-list">
                    <div v-for="alert in alerts" :key="alert.id" class="alert-item" :class="alert.severity">
                        <div class="alert-icon">{{ alert.icon }}</div>
                        <div class="alert-content">
                            <span class="alert-title">{{ alert.title }}</span>
                            <span class="alert-message">{{ alert.message }}</span>
                        </div>
                        <button class="alert-dismiss" @click="dismissAlert(alert.id)">×</button>
                    </div>
                </div>
            </div>

            <!-- Quick Actions -->
            <div class="quick-actions">
                <button class="action-btn-large" @click="sendTeamReminder">
                    <span class="action-icon">📢</span>
                    <span class="action-text">Send Team Reminder</span>
                </button>

                <button class="action-btn-large" @click="exportReport">
                    <span class="action-icon">📊</span>
                    <span class="action-text">Export Report</span>
                </button>

                <button class="action-btn-large" @click="viewMatchDayPrep" v-if="hasUpcomingMatch">
                    <span class="action-icon">⚽</span>
                    <span class="action-text">Match Day Prep</span>
                </button>
            </div>
        </template>

        <!-- Player Detail Modal -->
        <div v-if="selectedPlayerDetail" class="modal-overlay" @click.self="selectedPlayerDetail = null">
            <div class="player-detail-modal">
                <div class="modal-header">
                    <div class="player-header">
                        <div class="player-avatar large">
                            <img v-if="selectedPlayerDetail.image" :src="selectedPlayerDetail.image"
                                :alt="selectedPlayerDetail.name" />
                            <div v-else class="avatar-placeholder">
                                {{ getInitials(selectedPlayerDetail.name) }}
                            </div>
                        </div>
                        <div class="player-title">
                            <h3>{{ selectedPlayerDetail.name }}</h3>
                            <span class="player-position">{{ selectedPlayerDetail.position }}</span>
                        </div>
                    </div>
                    <button class="close-btn" @click="selectedPlayerDetail = null">×</button>
                </div>

                <div class="modal-content">
                    <!-- 7-Day History -->
                    <div class="history-section">
                        <h4>7-Day Compliance</h4>
                        <div class="history-grid">
                            <div v-for="day in selectedPlayerDetail.history" :key="day.date" class="history-day"
                                :class="{ logged: day.logged, partial: day.partial }">
                                <span class="day-name">{{ day.dayShort }}</span>
                                <span class="day-status">{{ day.logged ? '✓' : day.partial ? '◐' : '✗' }}</span>
                                <span class="day-meals">{{ day.meals }}/{{ day.targetMeals }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Macro Breakdown -->
                    <div class="macro-section">
                        <h4>Average Macros (7-day)</h4>
                        <div class="macro-comparison">
                            <div class="macro-item">
                                <span class="macro-label">Carbs</span>
                                <div class="macro-bar">
                                    <div class="bar-target" :style="{ width: '100%' }"></div>
                                    <div class="bar-actual"
                                        :style="{ width: selectedPlayerDetail.avgMacros.carbsPercent + '%' }"></div>
                                </div>
                                <span class="macro-values">
                                    {{ selectedPlayerDetail.avgMacros.carbs }}g / {{ selectedPlayerDetail.targets.carbs
                                    }}g
                                </span>
                            </div>
                            <div class="macro-item">
                                <span class="macro-label">Protein</span>
                                <div class="macro-bar">
                                    <div class="bar-target" :style="{ width: '100%' }"></div>
                                    <div class="bar-actual"
                                        :style="{ width: selectedPlayerDetail.avgMacros.proteinPercent + '%' }"></div>
                                </div>
                                <span class="macro-values">
                                    {{ selectedPlayerDetail.avgMacros.protein }}g / {{
                                    selectedPlayerDetail.targets.protein }}g
                                </span>
                            </div>
                            <div class="macro-item">
                                <span class="macro-label">Fat</span>
                                <div class="macro-bar">
                                    <div class="bar-target" :style="{ width: '100%' }"></div>
                                    <div class="bar-actual"
                                        :style="{ width: selectedPlayerDetail.avgMacros.fatPercent + '%' }">
                                    </div>
                                </div>
                                <span class="macro-values">
                                    {{ selectedPlayerDetail.avgMacros.fat }}g / {{ selectedPlayerDetail.targets.fat }}g
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Notes/Actions -->
                    <div class="actions-section">
                        <button class="btn-primary" @click="sendPlayerReminder(selectedPlayerDetail)">
                            Send Reminder
                        </button>
                        <button class="btn-secondary" @click="viewFullProfile(selectedPlayerDetail)">
                            View Full Profile
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Nutrition } from "@/services/nutrition-service";
import { mapGetters } from "vuex";

export default {
    name: "TeamComplianceDashboard",

    props: {
        clubId: {
            type: [Number, String],
            default: null,
        },
        teamId: {
            type: [Number, String],
            default: null,
        },
    },

    data() {
        return {
            loading: true,
            teams: [],
            selectedTeam: "",
            weekStart: this.getWeekStart(new Date()),
            sortBy: "compliance",
            showOnlyNeedsAttention: false,
            players: [],
            weeklyData: [],
            summaryData: {},
            alerts: [],
            selectedPlayerDetail: null,
        };
    },

    computed: {
        ...mapGetters(["getProfile"]),

        effectiveClubId() {
            return this.clubId || this.getProfile?.club_id;
        },

        formatDateRange() {
            const start = new Date(this.weekStart);
            const end = new Date(start);
            end.setDate(end.getDate() + 6);

            return `${this.formatDate(start)} - ${this.formatDate(end)}`;
        },

        isCurrentWeek() {
            const now = new Date();
            const currentWeekStart = this.getWeekStart(now);
            return this.weekStart >= currentWeekStart;
        },

        overallCompliance() {
            return this.summaryData.overall_compliance || 0;
        },

        complianceTrend() {
            const trend = this.summaryData.compliance_trend || 0;
            return trend > 0 ? "up" : trend < 0 ? "down" : "neutral";
        },

        complianceTrendIcon() {
            return this.complianceTrend === "up" ? "↑" : this.complianceTrend === "down" ? "↓" : "→";
        },

        complianceTrendValue() {
            return Math.abs(this.summaryData.compliance_trend || 0);
        },

        loggingRate() {
            return this.summaryData.logging_rate || 0;
        },

        loggedToday() {
            return this.summaryData.logged_today || 0;
        },

        totalPlayers() {
            return this.summaryData.total_players || this.players.length;
        },

        avgHydration() {
            return this.summaryData.avg_hydration || 0;
        },

        macroCompliance() {
            return this.summaryData.macro_compliance || 0;
        },

        hasUpcomingMatch() {
            return this.summaryData.upcoming_match !== null;
        },

        filteredPlayers() {
            let result = [...this.players];

            if (this.showOnlyNeedsAttention) {
                result = result.filter((p) => p.compliance < 70 || p.streak === 0);
            }

            switch (this.sortBy) {
                case "name":
                    result.sort((a, b) => a.name.localeCompare(b.name));
                    break;
                case "compliance":
                    result.sort((a, b) => a.compliance - b.compliance);
                    break;
                case "streak":
                    result.sort((a, b) => b.streak - a.streak);
                    break;
                case "hydration":
                    result.sort((a, b) => a.hydration - b.hydration);
                    break;
            }

            return result;
        },
    },

    mounted() {
        this.selectedTeam = this.teamId || "";
        this.loadData();
    },

    methods: {
        getWeekStart(date) {
            const d = new Date(date);
            const day = d.getDay();
            const diff = d.getDate() - day + (day === 0 ? -6 : 1);
            return new Date(d.setDate(diff)).toISOString().split("T")[0];
        },

        formatDate(dateStr) {
            const date = new Date(dateStr);
            return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
        },

        changeWeek(direction) {
            const current = new Date(this.weekStart);
            current.setDate(current.getDate() + direction * 7);
            this.weekStart = current.toISOString().split("T")[0];
            this.loadData();
        },

        async loadData() {
            this.loading = true;
            try {
                // Load team compliance data
                const endDate = new Date(this.weekStart);
                endDate.setDate(endDate.getDate() + 6);

                const response = await Nutrition.getTeamNutritionDashboard(
                    this.effectiveClubId,
                    this.selectedTeam || null,
                    {
                        start: this.weekStart,
                        end: endDate.toISOString().split("T")[0],
                    }
                );

                if (response.data?.success) {
                    this.summaryData = response.data.summary || {};
                    this.players = response.data.players || [];
                    this.weeklyData = this.buildWeeklyData(response.data.daily_trends || []);
                    this.alerts = this.generateAlerts(this.players);
                }

                // Load teams list if not loaded
                if (this.teams.length === 0) {
                    // Assuming teams are loaded elsewhere or from a teams service
                }
            } catch (error) {
                console.error("Error loading compliance data:", error);
            } finally {
                this.loading = false;
            }
        },

        buildWeeklyData(dailyTrends) {
            const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
            const data = [];
            const today = new Date().toISOString().split("T")[0];

            for (let i = 0; i < 7; i++) {
                const date = new Date(this.weekStart);
                date.setDate(date.getDate() + i);
                const dateStr = date.toISOString().split("T")[0];

                const dayData = dailyTrends.find((d) => d.date === dateStr) || {
                    compliance: 0,
                    loggingRate: 0,
                };

                data.push({
                    date: dateStr,
                    dayName: dayNames[date.getDay()],
                    dateShort: date.getDate().toString(),
                    compliance: dayData.compliance || 0,
                    loggingRate: dayData.logging_rate || dayData.loggingRate || 0,
                    isToday: dateStr === today,
                    isFuture: dateStr > today,
                });
            }

            return data;
        },

        generateAlerts(players) {
            const alerts = [];

            // Players not logging
            const notLogging = players.filter((p) => p.streak === 0 && p.compliance < 50);
            if (notLogging.length > 0) {
                alerts.push({
                    id: "not-logging",
                    severity: "warning",
                    icon: "⚠️",
                    title: `${notLogging.length} players not logging`,
                    message: notLogging.map((p) => p.name).slice(0, 3).join(", ") +
                        (notLogging.length > 3 ? ` and ${notLogging.length - 3} more` : ""),
                });
            }

            // Low hydration
            const lowHydration = players.filter((p) => p.hydration < 50);
            if (lowHydration.length > 0) {
                alerts.push({
                    id: "low-hydration",
                    severity: "info",
                    icon: "💧",
                    title: "Low hydration levels",
                    message: `${lowHydration.length} players below 50% hydration target`,
                });
            }

            return alerts;
        },

        getPlayerStatusClass(player) {
            if (player.compliance >= 80) return "good";
            if (player.compliance >= 50) return "moderate";
            if (player.compliance > 0) return "low";
            return "none";
        },

        getComplianceClass(compliance) {
            if (compliance >= 80) return "good";
            if (compliance >= 50) return "moderate";
            return "low";
        },

        getHydrationClass(hydration) {
            if (hydration >= 80) return "good";
            if (hydration >= 50) return "moderate";
            return "low";
        },

        getInitials(name) {
            return name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);
        },

        selectPlayer(player) {
            // Load detailed data for player
            this.selectedPlayerDetail = {
                ...player,
                history: this.generateMockHistory(),
                avgMacros: {
                    carbs: 280,
                    protein: 140,
                    fat: 70,
                    carbsPercent: 85,
                    proteinPercent: 90,
                    fatPercent: 95,
                },
                targets: {
                    carbs: 330,
                    protein: 155,
                    fat: 75,
                },
            };
        },

        generateMockHistory() {
            const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            return days.map((day, i) => ({
                date: `day-${i}`,
                dayShort: day,
                logged: Math.random() > 0.3,
                partial: Math.random() > 0.5,
                meals: Math.floor(Math.random() * 5) + 1,
                targetMeals: 5,
            }));
        },

        messagePlayer(player) {
            this.$emit("message-player", player);
        },

        viewPlayerDetail(player) {
            this.selectPlayer(player);
        },

        viewFullProfile(player) {
            this.$emit("view-profile", player);
        },

        sendPlayerReminder(player) {
            this.$emit("send-reminder", player);
        },

        sendTeamReminder() {
            this.$emit("team-reminder");
        },

        exportReport() {
            this.$emit("export-report", {
                weekStart: this.weekStart,
                teamId: this.selectedTeam,
                data: {
                    summary: this.summaryData,
                    players: this.players,
                    weekly: this.weeklyData,
                },
            });
        },

        viewMatchDayPrep() {
            this.$emit("match-day-prep");
        },

        dismissAlert(alertId) {
            this.alerts = this.alerts.filter((a) => a.id !== alertId);
        },

        clearAlerts() {
            this.alerts = [];
        },
    },
};
</script>

<style lang="scss" scoped>
.team-compliance-dashboard {
    padding: 16px;
}

.dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 16px;

    .header-title {
        h2 {
            margin: 0 0 4px 0;
            font-size: 1.3rem;
        }

        .date-range {
            font-size: 0.9rem;
            color: #666;
        }
    }

    .header-controls {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    .team-select {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 0.9rem;
    }

    .date-selector {
        display: flex;
        align-items: center;
        gap: 8px;

        .date-btn {
            width: 32px;
            height: 32px;
            border: 1px solid #ddd;
            border-radius: 6px;
            background: white;
            cursor: pointer;

            &:hover:not(:disabled) {
                background: #f0f0f0;
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }

        .current-week {
            font-size: 0.9rem;
            font-weight: 500;
        }
    }
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 20px;
    color: #666;

    .spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #eee;
        border-top-color: #28a745;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin-bottom: 16px;
    }
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.summary-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.summary-card {
    background: white;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;

    .card-icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
            width: 20px;
            height: 20px;
        }
    }

    .card-content {
        .card-value {
            display: block;
            font-size: 1.8rem;
            font-weight: 700;
        }

        .card-label {
            font-size: 0.85rem;
            color: #666;
        }
    }

    .card-trend {
        font-size: 0.85rem;
        font-weight: 600;

        &.up {
            color: #28a745;
        }

        &.down {
            color: #dc3545;
        }

        &.neutral {
            color: #6c757d;
        }
    }

    .card-detail {
        font-size: 0.8rem;
        color: #999;
    }

    &.overall {
        .card-icon {
            background: rgba(40, 167, 69, 0.1);
            color: #28a745;
        }

        .card-value {
            color: #28a745;
        }
    }

    &.logging {
        .card-icon {
            background: rgba(0, 123, 255, 0.1);
            color: #007bff;
        }

        .card-value {
            color: #007bff;
        }
    }

    &.hydration {
        .card-icon {
            background: rgba(23, 162, 184, 0.1);
            color: #17a2b8;
        }

        .card-value {
            color: #17a2b8;
        }
    }

    &.macros {
        .card-icon {
            background: rgba(253, 126, 20, 0.1);
            color: #fd7e14;
        }

        .card-value {
            color: #fd7e14;
        }
    }
}

.chart-section {
    background: white;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .section-header h3 {
        margin: 0 0 16px 0;
        font-size: 1rem;
    }
}

.compliance-chart {
    .chart-container {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        height: 150px;
        padding: 0 8px;
        margin-bottom: 8px;
    }

    .chart-day {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        max-width: 50px;

        &.today {
            .day-label {
                font-weight: 700;
                color: #28a745;
            }
        }

        &.future {
            opacity: 0.4;
        }

        .bar-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            height: 100px;
            margin-bottom: 4px;
        }

        .bar-stack {
            flex: 1;
            width: 24px;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            background: #f0f0f0;
            border-radius: 4px 4px 0 0;
            overflow: hidden;

            .bar {
                width: 100%;
                transition: height 0.3s;

                &.logging {
                    background: linear-gradient(180deg, #28a745, #20c997);
                }
            }
        }

        .bar-value {
            font-size: 0.7rem;
            font-weight: 600;
            color: #333;
        }

        .day-label {
            font-size: 0.75rem;
            color: #666;
        }

        .day-date {
            font-size: 0.7rem;
            color: #999;
        }
    }

    .chart-legend {
        display: flex;
        justify-content: center;
        gap: 16px;
        padding-top: 8px;
        border-top: 1px solid #eee;

        .legend-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 0.8rem;
            color: #666;

            .legend-color {
                width: 12px;
                height: 12px;
                border-radius: 2px;

                &.logging {
                    background: linear-gradient(135deg, #28a745, #20c997);
                }
            }
        }
    }
}

.players-section {
    background: white;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        flex-wrap: wrap;
        gap: 12px;

        h3 {
            margin: 0;
            font-size: 1rem;
        }

        .section-controls {
            display: flex;
            gap: 10px;
        }

        .sort-select {
            padding: 6px 10px;
            border: 1px solid #ddd;
            border-radius: 6px;
            font-size: 0.85rem;
        }

        .filter-btn {
            padding: 6px 12px;
            border: 1px solid #ddd;
            border-radius: 6px;
            background: white;
            font-size: 0.85rem;
            cursor: pointer;

            &:hover {
                background: #f8f9fa;
            }

            &.active {
                background: #fff3cd;
                border-color: #ffc107;
            }
        }
    }
}

.player-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 12px;
}

.player-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    border-left: 3px solid transparent;

    &:hover {
        background: #f0f0f0;
    }

    &.good {
        border-left-color: #28a745;
    }

    &.moderate {
        border-left-color: #ffc107;
    }

    &.low {
        border-left-color: #dc3545;
    }

    &.none {
        border-left-color: #6c757d;
    }

    .player-avatar {
        position: relative;
        flex-shrink: 0;

        img,
        .avatar-placeholder {
            width: 44px;
            height: 44px;
            border-radius: 50%;
            object-fit: cover;
        }

        .avatar-placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            background: #e0e0e0;
            color: #666;
            font-weight: 600;
            font-size: 0.9rem;
        }

        .status-indicator {
            position: absolute;
            bottom: 0;
            right: 0;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            border: 2px solid white;

            &.good {
                background: #28a745;
            }

            &.moderate {
                background: #ffc107;
            }

            &.low {
                background: #dc3545;
            }

            &.none {
                background: #6c757d;
            }
        }
    }

    .player-info {
        flex: 1;
        min-width: 0;

        .player-name {
            display: block;
            font-weight: 600;
            font-size: 0.95rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .player-position {
            font-size: 0.8rem;
            color: #666;
        }
    }

    .player-stats {
        flex: 1;
        min-width: 120px;

        .stat-row {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 4px;

            &:last-child {
                margin-bottom: 0;
            }

            .stat-label {
                font-size: 0.7rem;
                color: #999;
                width: 60px;
            }

            .compliance-bar {
                flex: 1;
                height: 4px;
                background: #e0e0e0;
                border-radius: 2px;
                overflow: hidden;

                .bar-fill {
                    height: 100%;
                    border-radius: 2px;

                    &.good {
                        background: #28a745;
                    }

                    &.moderate {
                        background: #ffc107;
                    }

                    &.low {
                        background: #dc3545;
                    }
                }
            }

            .stat-value {
                font-size: 0.8rem;
                font-weight: 600;
                min-width: 35px;
                text-align: right;

                &.streak {
                    color: #fd7e14;

                    .streak-icon {
                        font-size: 0.7rem;
                    }
                }

                &.good {
                    color: #28a745;
                }

                &.moderate {
                    color: #cc9a06;
                }

                &.low {
                    color: #dc3545;
                }
            }
        }
    }

    .player-actions {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .action-btn {
            width: 28px;
            height: 28px;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 0.9rem;
            display: flex;
            align-items: center;
            justify-content: center;

            &.message {
                background: #e3f2fd;
            }

            &.view {
                background: #e8f5e9;
            }

            &:hover {
                opacity: 0.8;
            }
        }
    }
}

.alerts-section {
    background: white;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;

        h3 {
            margin: 0;
            font-size: 1rem;
        }

        .clear-all-btn {
            padding: 4px 12px;
            border: none;
            background: #f0f0f0;
            border-radius: 12px;
            font-size: 0.8rem;
            cursor: pointer;
        }
    }

    .alerts-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .alert-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border-radius: 8px;

        &.warning {
            background: #fff3cd;
            border-left: 3px solid #ffc107;
        }

        &.info {
            background: #e3f2fd;
            border-left: 3px solid #2196f3;
        }

        .alert-icon {
            font-size: 1.2rem;
        }

        .alert-content {
            flex: 1;

            .alert-title {
                display: block;
                font-weight: 600;
                font-size: 0.9rem;
            }

            .alert-message {
                font-size: 0.8rem;
                color: #666;
            }
        }

        .alert-dismiss {
            width: 24px;
            height: 24px;
            border: none;
            background: transparent;
            font-size: 1.1rem;
            cursor: pointer;
            opacity: 0.6;

            &:hover {
                opacity: 1;
            }
        }
    }
}

.quick-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;

    .action-btn-large {
        flex: 1;
        min-width: 150px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 14px 20px;
        border: 2px solid #e0e0e0;
        border-radius: 10px;
        background: white;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            border-color: #28a745;
            background: rgba(40, 167, 69, 0.05);
        }

        .action-icon {
            font-size: 1.2rem;
        }

        .action-text {
            font-weight: 600;
            font-size: 0.9rem;
        }
    }
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.player-detail-modal {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 16px;
        border-bottom: 1px solid #eee;

        .player-header {
            display: flex;
            align-items: center;
            gap: 12px;

            .player-avatar.large {

                img,
                .avatar-placeholder {
                    width: 56px;
                    height: 56px;
                }
            }

            .player-title {
                h3 {
                    margin: 0;
                    font-size: 1.1rem;
                }

                .player-position {
                    font-size: 0.85rem;
                    color: #666;
                }
            }
        }

        .close-btn {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            border: none;
            background: #f0f0f0;
            font-size: 1.2rem;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .modal-content {
        padding: 16px;

        .history-section,
        .macro-section {
            margin-bottom: 20px;

            h4 {
                margin: 0 0 12px 0;
                font-size: 0.95rem;
            }
        }

        .history-grid {
            display: flex;
            gap: 8px;

            .history-day {
                flex: 1;
                text-align: center;
                padding: 8px;
                background: #f8f9fa;
                border-radius: 8px;

                &.logged {
                    background: #d4edda;

                    .day-status {
                        color: #28a745;
                    }
                }

                &.partial {
                    background: #fff3cd;

                    .day-status {
                        color: #856404;
                    }
                }

                .day-name {
                    display: block;
                    font-size: 0.7rem;
                    color: #666;
                }

                .day-status {
                    display: block;
                    font-size: 1rem;
                    font-weight: 700;
                }

                .day-meals {
                    display: block;
                    font-size: 0.65rem;
                    color: #999;
                }
            }
        }

        .macro-comparison {
            .macro-item {
                margin-bottom: 12px;

                .macro-label {
                    display: block;
                    font-size: 0.8rem;
                    font-weight: 500;
                    margin-bottom: 4px;
                }

                .macro-bar {
                    position: relative;
                    height: 8px;
                    background: #e0e0e0;
                    border-radius: 4px;
                    overflow: hidden;
                    margin-bottom: 4px;

                    .bar-target {
                        position: absolute;
                        height: 100%;
                        background: #e0e0e0;
                    }

                    .bar-actual {
                        position: relative;
                        height: 100%;
                        background: linear-gradient(90deg, #28a745, #20c997);
                        border-radius: 4px;
                    }
                }

                .macro-values {
                    font-size: 0.75rem;
                    color: #666;
                }
            }
        }

        .actions-section {
            display: flex;
            gap: 12px;

            button {
                flex: 1;
                padding: 12px;
                border: none;
                border-radius: 8px;
                font-weight: 600;
                cursor: pointer;
            }

            .btn-primary {
                background: linear-gradient(135deg, #28a745, #20c997);
                color: white;
            }

            .btn-secondary {
                background: #f0f0f0;
                color: #333;
            }
        }
    }
}

.no-players {
    text-align: center;
    padding: 40px;
    color: #666;
}
</style>
